import { useEffect } from "react";
import { createPortal } from "react-dom";
import type { Kind } from "@melo/shared";
import { store, useTables } from "../lib/store";
import { agents, byRank, people } from "../lib/select";
import { bulkEdit } from "../lib/actions";
import { clearSelection, useSelection } from "../lib/selection";
import { Avatar } from "./primitives";
import { DateInput, MenuButton, StatusDot, optionLabel } from "./pickers";
import { Icon } from "./icons";

/** Appears while items are selected. Every action applies to all of them at once. */
export function BulkBar({ spaceId, kinds }: { spaceId: string; kinds: Kind[] }) {
  const t = useTables();
  const sel = useSelection();
  const ids = [...sel].filter((id) => t.nodes.has(id));
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && !document.querySelector(".pop, .dialog, .panel") && clearSelection();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  useEffect(() => clearSelection, []);
  if (!ids.length) return null;
  const n = ids.length;
  const noun = `${n} item${n === 1 ? "" : "s"}`;
  const statuses = kinds.length === 1 ? kinds[0]!.statuses : (kinds.find((k) => k.key === "task") ?? kinds[0])?.statuses ?? [];
  const priority = kinds.flatMap((k) => k.fields).find((f) => f.key === "priority");
  const labels = [...t.labels.values()].filter((l) => l.spaceId === spaceId).sort(byRank);
  const members = new Set([...t.members.values()].filter((m) => m.spaceId === spaceId).map((m) => m.actorId));
  const folks = [...people(t), ...agents(t)].filter((a) => members.has(a.id) || a.role === "admin" || a.type === "agent");
  const milestones = [...t.nodes.values()].filter((x) => x.spaceId === spaceId && !x.draft && t.kinds.get(x.kindId)?.key === "phase").sort(byRank);
  const done = () => clearSelection();

  return createPortal(
    <div className="bulkbar" role="toolbar" aria-label={`Change ${noun}`}>
      <span className="bulk-count">
        <b>{n}</b> selected
      </span>
      <MenuButton
        label="Set status"
        className="btn sm"
        items={() =>
          statuses.map((s) => ({
            id: s.key,
            label: s.label,
            icon: (
              <span className="chip" style={{ border: 0, background: "none", padding: 0 }}>
                <StatusDot status={s} />
              </span>
            ),
            run: () => void bulkEdit(ids, { status: s.key }, `Moved ${noun} to ${s.label}`),
          }))
        }
      >
        Status
      </MenuButton>
      <MenuButton
        label="Assign"
        className="btn sm"
        searchable
        items={() => [
          { id: "none", label: "No one", icon: <Icon name="user" />, run: () => void bulkEdit(ids, { owners: [] }, `Unassigned ${noun}`) },
          ...folks.map((a) => ({ id: a.id, label: a.name, icon: <Avatar actor={a} size="sm" />, run: () => void bulkEdit(ids, { owners: [a.id] }, `Assigned ${noun} to ${a.name}`) })),
        ]}
      >
        Assign
      </MenuButton>
      {labels.length > 0 && (
        <MenuButton
          label="Add or remove a label"
          className="btn sm"
          items={() =>
            labels.flatMap((l) => {
              const all = ids.every((id) => store.t.nodes.get(id)?.labels.includes(l.id));
              return [
                {
                  id: l.id,
                  label: all ? `Remove ${l.name}` : `Add ${l.name}`,
                  icon: <span className="label-dot" style={{ background: l.color }} />,
                  run: () => void bulkEdit(ids, all ? { removeLabels: [l.id] } : { addLabels: [l.id] }, `${all ? "Removed" : "Added"} ${l.name} ${all ? "from" : "to"} ${noun}`),
                },
              ];
            })
          }
        >
          Label
        </MenuButton>
      )}
      {priority && (
        <MenuButton
          label="Set priority"
          className="btn sm"
          items={() => (priority.options ?? []).map((p) => ({ id: p, label: optionLabel(priority, p), run: () => void bulkEdit(ids, { fields: { priority: p } }, `Set ${noun} to ${p} priority`) }))}
        >
          Priority
        </MenuButton>
      )}
      <DateInput className="btn sm" label="Due date for the selected items" placeholder="Due" value={null} onSave={(v) => void bulkEdit(ids, { dueDate: v }, v ? `Set ${noun} due` : `Cleared the due date on ${noun}`)} />
      {milestones.length > 0 && (
        <MenuButton
          label="Move to a milestone"
          className="btn sm"
          searchable
          items={() => [
            { id: "none", label: "No milestone", run: () => void bulkEdit(ids, { parentId: null }, `Moved ${noun} out of their milestone`) },
            ...milestones.map((m) => ({ id: m.id, label: m.title, run: () => void bulkEdit(ids, { parentId: m.id }, `Moved ${noun} to ${m.title}`) })),
          ]}
        >
          Milestone
        </MenuButton>
      )}
      <button type="button" className="btn sm danger" onClick={() => void bulkEdit(ids, { archived: true }, `Archived ${noun}`).then(done)}>
        <Icon name="archive" /> Archive
      </button>
      <button type="button" className="btn ghost icon sm" aria-label="Clear selection" title="Clear selection (Esc)" onClick={done}>
        <Icon name="x" />
      </button>
    </div>,
    document.body,
  );
}
